

import React, { useEffect, useRef, useState } from "react";
import studentDropImage from "../../Assets/student_dummy_photo.png";
import studentDummyImage from "../../Assets/new_student_marker.png";
import startPoint from "../../Assets/Pin_icon_green50.png";
import endPoint from "../../Assets/Pin_icon50.png";
import carIcon from "../../Assets/live_car.png"; // Ensure this is a car icon image
// import carIcon from "../../Assets/bike4.png"; // Ensure this is a car icon image

import { convertLatLng } from "../CommonComponent/ConvertLatLng";

let minBounds = 1;
let maxBounds = 500;
let dividend = 10;

const TripInfoMap = ({ RIDER_DATA, driverPath }) => {
  const animationTimeoutRef = useRef(null);
  const carMarkerRef = useRef(null);
  const mapRef = useRef(null);
  const controlPanelRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Speed control state - default speed is 50ms per step (1x speed)
  const [speed, setSpeed] = useState(10); // Lower value = faster animation
  const [isPaused, setIsPaused] = useState(false); // Pause/play state
  const speedMultiplierRef = useRef(1); // Track speed multiplier (1x, 2x, 3x, etc.)

  // Store animation state in a way accessible to the global callback
  const animationStateRef = useRef({
    timeoutId: null,
    carMarker: null,
    step: 0,
    isActive: false,
    currentSpeed: 50, // Store current speed in animation state
    isPaused: false, // Store pause state
    currentStep: 0, // Store current step for resuming
    animateCarFunction: null, // Store reference to animateCar function for resume
  });

  // Update speed in animation state when speed changes
  useEffect(() => {
    animationStateRef.current.currentSpeed = speed;
  }, [speed]);

  // Update pause state in animation state when it changes
  useEffect(() => {
    animationStateRef.current.isPaused = isPaused;
  }, [isPaused]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Check if any element is in fullscreen
      const fullscreenElement = 
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      
      const isCurrentlyFullscreen = !!fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);

      // Move control panel to appropriate container
      if (controlPanelRef.current) {
        if (isCurrentlyFullscreen && fullscreenElement) {
          // In fullscreen: append to fullscreen element
          // Use a small delay to ensure Google Maps has set up its fullscreen structure
          setTimeout(() => {
            if (controlPanelRef.current && fullscreenElement) {
              // Check if already in fullscreen container
              if (fullscreenElement.contains(controlPanelRef.current)) {
                return; // Already in the right place
              }
              
              // Remove from current parent
              if (controlPanelRef.current.parentElement) {
                controlPanelRef.current.parentElement.removeChild(controlPanelRef.current);
              }
              
              // Append to fullscreen container
              fullscreenElement.appendChild(controlPanelRef.current);
            }
          }, 100);
        } else {
          // Not in fullscreen: move back to original container
          const originalContainer = document.querySelector('[data-map-container]');
          if (originalContainer && controlPanelRef.current) {
            // Check if already in original container
            if (originalContainer.contains(controlPanelRef.current)) {
              return; // Already in the right place
            }
            
            // Remove from current parent
            if (controlPanelRef.current.parentElement) {
              controlPanelRef.current.parentElement.removeChild(controlPanelRef.current);
            }
            
            // Append to original container
            originalContainer.appendChild(controlPanelRef.current);
          }
        }
      }
    };

    // Listen to fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Initial check
    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    // Clear any existing animation timeout
    if (animationStateRef.current.timeoutId) {
      clearTimeout(animationStateRef.current.timeoutId);
      animationStateRef.current.timeoutId = null;
    }
    animationStateRef.current.step = 0;
    animationStateRef.current.carMarker = null;
    animationStateRef.current.isActive = false;
    animationStateRef.current.currentSpeed = speed; // Initialize speed

    // Clear the map div
    const mapElement = document.getElementById("map");
    if (mapElement) {
      mapElement.innerHTML = "";
    }

    // Create a closure to capture current RIDER_DATA and driverPath
    const currentRiderData = RIDER_DATA;
    const currentDriverPath = driverPath;
    const currentAnimationState = animationStateRef.current;

    // Create unique callback name
    const callbackName = `initMap_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Define the initialization function with closure
    window[callbackName] = function myInitMap() {
      // Mark animation as active
      currentAnimationState.isActive = true;
      
      // Clear any existing animation timeout before starting new one
      if (currentAnimationState.timeoutId) {
        clearTimeout(currentAnimationState.timeoutId);
        currentAnimationState.timeoutId = null;
      }
      currentAnimationState.step = 0;

      // Clear the map div to ensure clean state
      const mapElement = document.getElementById("map");
      if (!mapElement) {
        delete window[callbackName];
        return;
      }
      
      if (mapElement.innerHTML) {
        mapElement.innerHTML = "";
      }

      initializeMap(mapElement, currentRiderData, currentDriverPath, currentAnimationState);
      
      // Clean up callback after use
      delete window[callbackName];
    };

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      // Maps already loaded, initialize directly
      window[callbackName]();
    } else {
      const script = document.createElement("script");
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=AIzaSyDHdkmGjsfNqasFs6m9CooShFZsqWHcdUs&callback=${callbackName}&libraries=places&v=weekly`;
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Cleanup: Clear animation timeout and mark as inactive
      if (animationStateRef.current.timeoutId) {
        clearTimeout(animationStateRef.current.timeoutId);
        animationStateRef.current.timeoutId = null;
      }
      animationStateRef.current.isActive = false;
      
      // Clean up callback if it exists
      if (window[callbackName]) {
        delete window[callbackName];
      }
    };
  }, [RIDER_DATA, driverPath]);

  // Function to calculate bearing between two points
  const calculateBearing = (start, end) => {
    const startLat = (start.lat * Math.PI) / 180;
    const startLng = (start.lng * Math.PI) / 180;
    const endLat = (end.lat * Math.PI) / 180;
    const endLng = (end.lng * Math.PI) / 180;

    const y = Math.sin(endLng - startLng) * Math.cos(endLat);
    const x =
      Math.cos(startLat) * Math.sin(endLat) - 
      Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);
    const bearing = (Math.atan2(y, x) * 180) / Math.PI;
    return (bearing + 360) % 360; // Normalize to 0-360 degrees
  };

  const initializeMap = (mapElement, RIDER_DATA, driverPath, animationState) => {
    if (!mapElement) return;

    const map = new window.google.maps.Map(mapElement, {
      zoom: 11,
      center:
        RIDER_DATA?.TripStatus?.toLowerCase() !== "ended"
          ? {
              lat: +RIDER_DATA?.PickupLatLng?.split(",")[0],
              lng: +RIDER_DATA?.PickupLatLng?.split(",")[1],
            }
          : {
              lat: RIDER_DATA?.ActualPickupLatLng?.split(",")[0],
              lng: RIDER_DATA?.ActualPickupLatLng?.split(",")[1],
            },
      disableDefaultUI: true,
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_TOP,
      },
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_CENTER,
      },
    });

    if (driverPath) {
      let currentBounds = Math.ceil(driverPath.length / dividend);
      while (!(currentBounds < maxBounds && currentBounds >= minBounds)) {
        if (currentBounds < minBounds)
          currentBounds = Math.ceil(
            driverPath?.length / (Math.ceil(dividend) / 2)
          );
        else if (currentBounds > maxBounds)
          currentBounds = Math.ceil(
            driverPath?.length / (Math.ceil(dividend) * 2)
          );
      }

      var bounds = new window.google.maps.LatLngBounds();
      for (let i = 0; i < driverPath?.length; i += currentBounds) {
        const point = driverPath[i];
        if (point?.LL) {
          const [lat, lng] = point.LL.split(",").map(Number);
          bounds.extend(new window.google.maps.LatLng(lat, lng));
        }
      }

      const [lastLat, lastLng] =
        driverPath[driverPath?.length - 1]?.LL?.split(",")?.map(Number);
      if (lastLat && lastLng) {
        bounds.extend(new window.google.maps.LatLng(lastLat, lastLng));
      }

      map.fitBounds(bounds);
    }

    const flightPlanCoordinates = driverPath?.map((point) => {
      if (point?.LL) {
        const [lat, lng] = point.LL.split(",").map((coord) => parseFloat(coord));
        return { lat, lng };
      }
      return null; // Skip invalid LL
    }).filter((coord) => coord !== null); // Filter out null values

    const flightPathBorder = new window.google.maps.Polyline({
      path: flightPlanCoordinates,
      geodesic: true,
      strokeColor: "black",
      strokeOpacity: 1.0,
      strokeWeight: 6,
    });

    const flightPath = new window.google.maps.Polyline({
      path: flightPlanCoordinates,
      geodesic: true,
      strokeColor: "#00b0ff",
      // strokeColor: "#4CAF50",
      // strokeColor: "#00b0ff",
      strokeOpacity: 1.0,
      strokeWeight: 5,
    });

    flightPath.setMap(map);
    flightPathBorder.setMap(map);

    const infoWindow = new window.google.maps.InfoWindow();
    const pickupLatLng =
      convertLatLng(RIDER_DATA?.ActualPickupLatLng) ||
      convertLatLng(RIDER_DATA?.PickupLatLng);
    const pickupName =
      RIDER_DATA?.ActualPickupName || RIDER_DATA?.PickupAddress;

    const dropLatLng =
      convertLatLng(RIDER_DATA?.ActualDropOffLatLng) ||
      convertLatLng(RIDER_DATA?.DropOffLatLng);
    const dropName =
      RIDER_DATA?.ActualDropOffName || RIDER_DATA?.DropOffAddress;

    const marker1 = new window.google.maps.Marker({
      position: new window.google.maps.LatLng(pickupLatLng),
      map,
      myTitle: pickupName,
      icon: startPoint,
      optimized: false,
    });

    const marker2 = new window.google.maps.Marker({
      position: new window.google.maps.LatLng(dropLatLng),
      map,
      myTitle: dropName,
      icon: endPoint,
      optimized: false,
    });

    marker1.addListener("mouseover", () => {
      infoWindow.close();
      infoWindow.setHeaderContent(marker1.myTitle);
      infoWindow.open(marker1.getMap(), marker1);
    });

    marker2.addListener("mouseover", () => {
      infoWindow.close();
      infoWindow.setHeaderContent(marker2.myTitle);
      infoWindow.open(marker2.getMap(), marker2);
    });

    // Add a car marker
    if (flightPlanCoordinates.length > 0) {
      // Create car marker with image icon
      const carIconConfig = {
        url: carIcon,
        scaledSize: new window.google.maps.Size(36, 36),

        
        
        anchor: new window.google.maps.Point(18, 18),
      };

      const carMarker = new window.google.maps.Marker({
        position: flightPlanCoordinates[0],
        map,
        icon: carIconConfig,
        optimized: false,
        zIndex: 1000, // Ensure car marker is on top
      });

      // Store car marker reference and starting position
      animationState.carMarker = carMarker;
      animationState.startPosition = flightPlanCoordinates[0]; // Store starting position for restart
      
      // Store reference to the image element for rotation
      let carMarkerImageElement = null;
      
      // Function to find and store the car marker image element
      const findCarMarkerImage = () => {
        if (carMarkerImageElement) return carMarkerImageElement;

        const mapDiv = document.getElementById("map");
        if (!mapDiv) return null;

        const images = mapDiv.getElementsByTagName("img");
        for (let i = 0; i < images.length; i++) {
          const img = images[i];
          // Check if this image matches our car icon file name
          const fileName = carIcon.split("/").pop() || carIcon.split("\\").pop();
          if (img.src && (img.src.includes("live_car") || (fileName && img.src.includes(fileName)))) {
            // Ensure the marker image uses the intended size and isn't overridden by global CSS
            try {
              img.style.width = "50px";
              img.style.height = "100px";
              img.style.maxWidth = "none";
              img.style.maxHeight = "none";
              img.style.objectFit = "contain";
              img.style.display = "block";
            } catch (e) {
              // ignore styling errors
            }

            carMarkerImageElement = img;
            return img;
          }
        }
        return null;
      };

      // Function to interpolate between two points for smoother movement
      const interpolatePoint = (start, end, fraction) => {
        return {
          lat: start.lat + (end.lat - start.lat) * fraction,
          lng: start.lng + (end.lng - start.lng) * fraction,
        };
      };

      // Function to animate the car along the path
      const numSteps = flightPlanCoordinates.length;
      // Use speed from animationState (which gets updated from state)
      const currentSpeed = animationState.currentSpeed || 50; // Default to 50ms if not set
      const interpolationSteps = 5; // Number of intermediate steps between each coordinate point

      // Initialize currentStep from animationState (for restart support)
      if (animationState.currentStep === undefined || animationState.currentStep === null) {
        animationState.currentStep = 0;
      }
      let currentStep = animationState.currentStep;
      let lastBearing = null; // Store last bearing to avoid unnecessary rotation changes
      const bearingThreshold = 2; // Only update rotation if bearing changes by more than 2 degrees

      const animateCar = () => {
        // Check if animation is still active and not paused
        if (!animationState.isActive || !animationState.carMarker || animationState.isPaused) {
          return;
        }

        // Read currentStep from animationState (supports restart)
        currentStep = animationState.currentStep || 0;
        const step = Math.floor(currentStep);
        if (step < numSteps - 1) {
          const currentPosition = flightPlanCoordinates[step];
          const nextPosition = flightPlanCoordinates[step + 1];
          
          // Calculate interpolation fraction (0 to 1)
          const fraction = currentStep - step;
          
          // Interpolate position for smoother movement
          const interpolatedPosition = interpolatePoint(currentPosition, nextPosition, fraction);
          
          // Calculate bearing - use a look-ahead point for more stable direction on straight lines
          // Look ahead a few steps to get a more stable bearing
          const lookAheadStep = Math.min(step + 2, numSteps - 1);
          const lookAheadPosition = flightPlanCoordinates[lookAheadStep];
          
          // Calculate bearing between current and look-ahead position for stability
          const bearing = calculateBearing(
            interpolatedPosition,
            lookAheadPosition
          );

          // Update car position smoothly
          animationState.carMarker.setPosition(interpolatedPosition);

          // Only update rotation if bearing has changed significantly
          // This prevents jittery rotation on straight lines
          if (lastBearing === null || Math.abs(bearing - lastBearing) > bearingThreshold) {
            lastBearing = bearing;
            
            // Rotate the car icon using CSS transform
            // Find the car marker image element
            const carImg = findCarMarkerImage();
            if (carImg) {
              carImg.style.transform = `rotate(${bearing}deg)`;
              carImg.style.transformOrigin = 'center center';
              carImg.style.transition = 'transform 0.2s ease-out';
            } else {
              // If not found, try again after a short delay
              setTimeout(() => {
                const carImgRetry = findCarMarkerImage();
                if (carImgRetry) {
                  carImgRetry.style.transform = `rotate(${bearing}deg)`;
                  carImgRetry.style.transformOrigin = 'center center';
                  carImgRetry.style.transition = 'transform 0.2s ease-out';
                }
              }, 10);
            }
          }

          // Increment step with interpolation
          currentStep += 1 / interpolationSteps;
          animationState.step = Math.floor(currentStep);
          animationState.currentStep = currentStep; // Save current step for resume
          // Use current speed from animationState
          animationState.timeoutId = setTimeout(animateCar, animationState.currentSpeed || 50);
        }
      };

      // Store animateCar function reference for pause/resume
      animationState.animateCarFunction = animateCar;
      
      // Start the animation after a short delay to ensure marker is rendered
      setTimeout(() => {
        findCarMarkerImage(); // Try to find the image element
        if (!animationState.isPaused) {
          animateCar();
        }
      }, 100);
    }
  };

  // Speed control functions
  // Speed levels configuration: [speed in ms, multiplier label]
  const speedLevels = [
    { ms: 50, label: "1x" },
    { ms: 25, label: "2x" },
    { ms: 10, label: "5x" },
    { ms: 5, label: "10x" },
  ];

  const setSpeedLevel = (speedMs) => {
    setSpeed(speedMs);
    speedMultiplierRef.current = Math.round(50 / speedMs);
  };

  const togglePause = () => {
    const newPausedState = !isPaused;
    setIsPaused(newPausedState);
    
    // If pausing, clear the timeout
    if (newPausedState) {
      if (animationStateRef.current.timeoutId) {
        clearTimeout(animationStateRef.current.timeoutId);
        animationStateRef.current.timeoutId = null;
      }
    } else {
      // If resuming, restart the animation from where it paused
      if (animationStateRef.current.isActive && animationStateRef.current.carMarker && animationStateRef.current.animateCarFunction) {
        // Clear any existing timeout
        if (animationStateRef.current.timeoutId) {
          clearTimeout(animationStateRef.current.timeoutId);
        }
        // Resume animation by calling the stored function
        setTimeout(() => {
          animationStateRef.current.animateCarFunction();
        }, 10);
      }
    }
  };

  const restartAnimation = () => {
    // Clear any existing timeout
    if (animationStateRef.current.timeoutId) {
      clearTimeout(animationStateRef.current.timeoutId);
      animationStateRef.current.timeoutId = null;
    }
    
    // Reset animation state
    animationStateRef.current.step = 0;
    animationStateRef.current.currentStep = 0;
    
    // Reset pause state to false (unpause if paused)
    setIsPaused(false);
    
    // Move car marker back to starting position if it exists
    if (animationStateRef.current.carMarker && animationStateRef.current.startPosition) {
      animationStateRef.current.carMarker.setPosition(animationStateRef.current.startPosition);
    }
    
    // Restart animation if active
    if (animationStateRef.current.isActive && animationStateRef.current.carMarker && animationStateRef.current.animateCarFunction) {
      // Clear any existing timeout
      if (animationStateRef.current.timeoutId) {
        clearTimeout(animationStateRef.current.timeoutId);
      }
      // Restart animation from beginning
      setTimeout(() => {
        animationStateRef.current.animateCarFunction();
      }, 10);
    }
  };

  const currentSpeedMultiplier = Math.round(50 / speed);

  return (
    <div 
      data-map-container
      style={{ position: "relative", height: "calc(100vh - 120px)", width: "100%" }}
    >
      <div id="map" style={{ height: "100%", width: "100%" }}></div>
      
      {/* Speed Control Panel */}
      <div
        ref={controlPanelRef}
        style={{
          position: "absolute",
          top: "10px",
          left: "10px",
          zIndex: 10000,
          background: "white",
          padding: "8px",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "row",
          gap: "6px",
          alignItems: "center",
          pointerEvents: "auto",
          willChange: "transform",
        }}
      >
        {/* Pause/Play Button */}
        <button
          onClick={togglePause}
          style={{
            padding: "5px 8px",
            backgroundColor: isPaused ? "#4CAF50" : "#ff9800",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            width: "32px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = isPaused ? "#45a049" : "#f57c00";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = isPaused ? "#4CAF50" : "#ff9800";
          }}
        >
          {isPaused ? "▶" : "⏸"}
        </button>

        {/* Restart Button */}
        <button
          onClick={restartAnimation}
          style={{
            padding: "5px 8px",
            backgroundColor: "#e0e0e0",
            color: "red", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            width: "32px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#d0d0d0";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#e0e0e0";
          }}
          title="Restart animation"
        >
          ⏹
        </button>

        {/* Speed Buttons */}
        {speedLevels.map((level) => {
          const isActive = speed === level.ms;
          return (
            <button
              key={level.label}
              onClick={() => setSpeedLevel(level.ms)}
              style={{
                padding: "5px 8px",
                backgroundColor: isActive ? "#2196F3" : "#f0f0f0",
                color: isActive ? "white" : "#333",
                border: isActive ? "none" : "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "600",
                minWidth: "32px",
                height: "28px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.target.style.backgroundColor = "#e0e0e0";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.target.style.backgroundColor = "#f0f0f0";
                }
              }}
            >
              {level.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TripInfoMap;
